import { PayrollFrequency } from '@prisma/client';
import type { WeekdayNumbers } from 'luxon';
import type { Context } from '~/server/context';
import { BaseDateHelper } from '../../base.date.helper';

export { SINGLE_DAY_LUXON_DATE_FORMAT } from '../../base.date.helper';
export type { ITimestampedDBObj, ScheduledJobWithTimezone } from '../../base.date.helper';

export class DateHelper extends BaseDateHelper {
	// Keep in mind that during serialization timezones will be dropped, and these
	// dates will be sent back to the client as UTC.
	public static translateDatesForScheduledJob<T extends Parameters<typeof BaseDateHelper.translateDates>[0]>(scheduledJob: T, ctx: Context) {
		this.translateDates(scheduledJob, ctx.timezone);
	}

	public static getIsOnBusinessLocalDateQuery(date: Date, timezone: string) {
		const businessLocalDate = DateHelper.getBusinessLocalDateTime(date, timezone);
		return {
			gte: businessLocalDate.startOf('day').toJSDate(),
			lte: businessLocalDate.endOf('day').toJSDate(),
		};
	}

	public static getIsBetweenBusinessLocalDatesQuery(startDate: Date, endDate: Date, timezone: string) {
		return {
			gte: DateHelper.getBusinessLocalDateTime(startDate, timezone).startOf('day').toJSDate(),
			lte: DateHelper.getBusinessLocalDateTime(endDate, timezone).endOf('day').toJSDate(),
		};
	}

	public static getStartAndEndDatesByPayPeriod({
		date,
		payrollFrequency,
		payPeriodStartDay,
		isBiweeklyOffset,
		isLastPayPeriod,
	}: {
		date: Parameters<typeof BaseDateHelper.getWeekFilter>[0];
		payrollFrequency: PayrollFrequency;
		payPeriodStartDay: WeekdayNumbers;
		isBiweeklyOffset?: boolean;
		isLastPayPeriod?: boolean;
	}) {
		if (!isLastPayPeriod) {
			switch (payrollFrequency) {
				case PayrollFrequency.WEEKLY:
					return DateHelper.getWeekFilter(date, payPeriodStartDay as WeekdayNumbers);
				case PayrollFrequency.BIWEEKLY:
					return DateHelper.getBiweeklyFilter(date, payPeriodStartDay as WeekdayNumbers, isBiweeklyOffset);
				case PayrollFrequency.BIMONTHLY:
					return DateHelper.getHalfOfMonthFilter(date);
				case PayrollFrequency.MONTHLY:
					return DateHelper.getMonthFilter(date);
			}
		}

		switch (payrollFrequency) {
			case PayrollFrequency.WEEKLY:
				return DateHelper.getWeekFilter(date, payPeriodStartDay as WeekdayNumbers, true);
			case PayrollFrequency.BIWEEKLY:
				return DateHelper.getBiweeklyFilter(date, payPeriodStartDay as WeekdayNumbers, isBiweeklyOffset, true);
			case PayrollFrequency.BIMONTHLY:
				return DateHelper.getHalfOfMonthFilter(date, true);
			case PayrollFrequency.MONTHLY:
				return DateHelper.getMonthFilter(date, true);
		}
	}
}
